import React, { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";

export const uploadDealerDocument = async (dealerId, file, label) => {
  const fd = new FormData(); fd.append("file", file);
  await api.post(`/dealers/${dealerId}/documents?label=${encodeURIComponent(label || file.name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
};

export const SignatureOnFileDialog = ({ dealer, open, onClose, onDone }) => {
  const legacy = dealer?.agreement_status === "dealer_signed" || dealer?.status === "dealer_signed";
  const [form, setForm] = useState({ signed_at: "", signer_name: "", file: null });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ signed_at: new Date().toISOString().slice(0, 10), signer_name: "", file: null }); }, [open]);
  if (!dealer) return null;
  const id = dealer.dealer_id || dealer.id;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dealers/${id}/mark-signed`, { signed_at: form.signed_at || undefined, signer_name: form.signer_name || undefined, note: legacy ? "Countersign waived" : "Signature on file" });
      let uploaded = true;
      if (form.file) { try { await uploadDealerDocument(id, form.file, `Signed agreement (paper) — ${form.file.name}`); } catch { uploaded = false; } }
      toast.success(uploaded ? (legacy ? "Finalized — dealer is in the pipeline" : "Signature on file — dealer is in the pipeline") : "Marked signed, but the file upload failed — add it from the dealer profile");
      onDone?.(data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update agreement"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="signature-on-file-dialog">
        <DialogHeader><DialogTitle>{legacy ? "Remove countersign & finalize" : "Signature on file"} — {dealer.dealer_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6C757D]">{legacy ? "The dealer has already signed. This executes the agreement without a countersign and pushes the dealer into the pipeline." : "No e-sign request will be sent. The agreement is marked executed and the dealer goes straight into the onboarding pipeline."}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {!legacy && <>
            <div><Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Date signed</Label><Input type="date" value={form.signed_at} onChange={(e) => setForm({ ...form, signed_at: e.target.value })} className="h-9 rounded-sm mt-1" data-testid="sof-date" /></div>
            <div><Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Signed by</Label><Input value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} placeholder="Dealer principal" className="h-9 rounded-sm mt-1" data-testid="sof-name" /></div>
          </>}
          <div className="sm:col-span-2"><Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Scanned signed agreement (optional)</Label>
            <label className="mt-1 flex items-center gap-2 h-10 px-3 border border-dashed border-[#0B132B]/30 rounded-sm text-xs text-[#0B132B] cursor-pointer hover:bg-[#F8F9FA]">
              <Paperclip size={13} /> {form.file ? form.file.name : "Attach PDF or image — it's saved to the dealer file"}
              <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="sof-file" />
            </label></div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="h-9 rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="h-9 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="sof-confirm">{busy ? "Saving…" : "Confirm & push to pipeline"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
